import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ArrowPagination, ReactTable } from "components";
import "modules/LocationLicenseOverview/components/InvoiceTable/InvoiceTable.scss";
import { formatPrice } from "utils/formatPrice";

import type { HistoricalInvoice, PaginatedHistoricalInvoices } from "modules/LocationLicenseOverview/queryKeys";

type InvoiceTableProps = {
  isLoadingInvoices: boolean;
  currentInvoicePage: number;
  isRefetchingInvoices: boolean;
  invoices?: PaginatedHistoricalInvoices;
  onPageChange: (pageIndex: number) => void;
  onInvoiceRowClick: (rowData: HistoricalInvoice) => void;
};

const columnHelper = createColumnHelper<HistoricalInvoice>();

export const InvoiceTable = ({ invoices, onInvoiceRowClick, isLoadingInvoices, currentInvoicePage, onPageChange, isRefetchingInvoices }: InvoiceTableProps) => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "subject",
        header: t("v8_invoice_name", "Invoice name"),
        cell: ({ row: { original } }) => <span>{original.Subject}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "modified",
        header: t("v8_invoice_date", "Invoice date"),
        cell: ({ row: { original } }) => <span>{original.Modified}</span>
      }),
      columnHelper.accessor(row => row, {
        id: "amount_fc",
        header: t("v8_total_price", "Total price"),
        cell: ({ row: { original } }) => <span>{`€ ${formatPrice(original.AmountFC)}`}</span>
      })
    ],
    [t]
  );

  return (
    <div className="InvoiceTable">
      <ReactTable
        isFetching={isLoadingInvoices || isRefetchingInvoices}
        columns={columns}
        data={invoices?.results}
        emptyRowMessage={t("v8_no_invoices_found", "No invoices found")}
        onRowClick={onInvoiceRowClick}
      />

      {!(!invoices?.__next && currentInvoicePage === 0) && (
        <ArrowPagination
          disabledNext={!invoices?.__next}
          disabledPrevious={currentInvoicePage === 0}
          onClickNext={() => onPageChange(currentInvoicePage + 1)}
          onClickPrevious={() => onPageChange(currentInvoicePage + -1)}
        />
      )}
    </div>
  );
};
